import { Backdrop, CircularProgress, Theme, useMediaQuery } from '@mui/material';
import { FC, PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { useInterval } from '~/core';
import { useTheme } from '~/themes';

type PlatformType = 'ios' | 'android' | 'desktop' | 'unknown';

const AppContext = createContext<AppContextData>({
  displayNavbar: false,
  displayFooter: false,
} as AppContextData);

const AppProvider: FC<PropsWithChildren> = ({ children }) => {
  const { COLORS } = useTheme();
  const [platform, setPlatform] = useState<PlatformType>('unknown');

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  // detect platform
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const platform = window.navigator.platform?.toLowerCase() || '';

    if (/iphone|ipad|ipod/.test(userAgent) || /iphone|ipad|ipod/.test(platform)) {
      setPlatform('ios');
    } else if (/android/.test(userAgent)) {
      setPlatform('android');
    } else if (/win|mac|linux/.test(platform)) {
      setPlatform('desktop');
    } else {
      setPlatform('unknown');
    }
  }, []);

  const isDesktop = platform === 'desktop';
  const isIOS = platform === 'ios';
  const isAndroid = platform === 'android';
  // detect platform

  // loading state management
  const [isGlobalLoading, setGlobalLoading] = useState(false);
  const [displayingLoader, setDisplayingLoader] = useState(0);
  const isLoading = isGlobalLoading || displayingLoader > 0;

  useInterval(() => {
    if (displayingLoader > 0) {
      setDisplayingLoader(displayingLoader - 1);
    }
  }, 1000);
  // end: loading state management

  // navigation components visibility management
  const [displayNavbar, setDisplayNavbar] = useState<boolean>(false);
  const [displayFooter, setDisplayFooter] = useState<boolean>(true);
  // end: navigation components visibility management

  return (
    <AppContext.Provider
      value={{
        displayNavbar,
        setDisplayNavbar,
        displayFooter,
        setDisplayFooter,
        // others
        env: process.env.ENV,
        // ui
        isMobile,
        // loading
        setGlobalLoading,
        isLoading,
        // platform
        platform,
        isDesktop,
        isIOS,
        isAndroid,
      }}
    >
      <Backdrop
        sx={{ background: `${COLORS.others.white}99`, zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color='primary' />
      </Backdrop>
      {children}
    </AppContext.Provider>
  );
};

interface AppContextData {
  displayNavbar: boolean;
  setDisplayNavbar: (state: boolean) => void;
  displayFooter: boolean;
  setDisplayFooter: (state: boolean) => void;
  // others
  env: string | undefined;
  // ui
  isMobile: boolean;
  // loading
  setGlobalLoading: (state: boolean) => void;
  isLoading: boolean;
  // platform
  platform: PlatformType;
  isDesktop: boolean;
  isIOS: boolean;
  isAndroid: boolean;
}

const useAppContext = (): AppContextData => useContext(AppContext);

export { AppProvider, useAppContext };
